import React, { useCallback, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { cn } from './Table';

// Zona de carga de imagen con TRES formas de usarla, porque cada una gana en un contexto
// distinto: arrastrar el archivo (lo más rápido cuando ya lo tenés en una carpeta),
// click para abrir el explorador (lo que todos esperan), y pegar del portapapeles
// (ideal cuando copiás la foto del catálogo del proveedor desde el navegador).
//
// Muestra la imagen actual como fondo y los controles encima al pasar el mouse, así la
// grilla no cambia de tamaño entre "con foto" y "sin foto".
//
// No sube nada por sí sola: avisa con `onFile` y quien la use decide a dónde va. Así la
// misma pieza sirve para insumos, productos o lo que venga.

export function ImageDrop({
    src, alt, onFile, onClear, busy, size = 'md', className, hint,
}: {
    src?: string | null;
    alt?: string;
    onFile: (file: File) => void;
    onClear?: () => void;
    busy?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    hint?: string;
}) {
    const [over, setOver] = useState(false);
    const input = useRef<HTMLInputElement>(null);

    const SIZES = {
        sm: 'h-10 w-10 rounded-lg',   // entra en una fila de 52px del DataGrid
        md: 'h-20 w-20 rounded-xl',
        lg: 'h-32 w-32 rounded-2xl',
    } as const;

    const take = useCallback((files: FileList | null | undefined) => {
        const f = Array.from(files || []).find(x => x.type.startsWith('image/'));
        if (f) onFile(f);
    }, [onFile]);

    return (
        <div
            className={cn(
                'group relative shrink-0 overflow-hidden border bg-ground transition-colors',
                SIZES[size],
                over ? 'border-brand border-dashed bg-brand/5' : 'border-line border-dashed',
                busy ? 'cursor-wait' : 'cursor-pointer',
                className,
            )}
            title={hint || 'Arrastrá una foto, hacé click o pegá del portapapeles'}
            onClick={() => !busy && input.current?.click()}
            // tabIndex + onPaste: para pegar hay que poder enfocar el elemento
            tabIndex={0}
            onPaste={(e) => take(e.clipboardData?.files)}
            onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer?.files); }}
        >
            {src && !over && (
                <img src={src} alt={alt || ''} loading="lazy" className="h-full w-full max-w-full object-cover" />
            )}

            {/* Capa de controles: visible si no hay foto, al pasar el mouse, o arrastrando */}
            <div className={cn(
                'absolute inset-0 grid place-items-center gap-0.5 text-center transition-opacity',
                src && !over ? 'bg-black/55 opacity-0 group-hover:opacity-100' : 'opacity-100',
            )}>
                {busy
                    ? <Loader2 size={size === 'sm' ? 14 : 18} className={cn('spin', src ? 'text-white' : 'text-faint')} />
                    : <>
                        <ImagePlus size={size === 'sm' ? 14 : 18} className={src && !over ? 'text-white' : 'text-faint'} />
                        {size !== 'sm' && (
                            <span className={cn('px-1 text-[0.58rem] font-semibold leading-tight', src && !over ? 'text-white' : 'text-faint')}>
                                {over ? 'Soltá acá' : src ? 'Cambiar' : 'Arrastrá o click'}
                            </span>
                        )}
                    </>}
            </div>

            {src && onClear && !busy && (
                <button
                    type="button"
                    title="Quitar la foto"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    className="absolute right-0.5 top-0.5 hidden rounded-md bg-black/60 p-0.5 text-white group-hover:block">
                    <Trash2 size={11} />
                </button>
            )}

            <input ref={input} type="file" accept="image/*" className="hidden"
                onChange={(e) => { take(e.target.files); e.currentTarget.value = ''; }} />
        </div>
    );
}
